import { requestResetPassword } from '@app/services/auth';
import { forgotPasswordValidation } from '@app/utils/validations';
import { useEffect, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

export const useForgotPasswordPage = () => {
  const forgotPassordForm = useForm({ resolver: forgotPasswordValidation });
  const [emailSent, setEmailSent] = useState(false);
  const [wasResent, setWasResent] = useState(false);
  const [disableResend, setDisableResend] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState<number>(0);
  const intervalRef = useRef<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');
  const navigate = useNavigate();

  const userEmail = forgotPassordForm.watch('email');

  const handleSendReset = () => {
    setEmailSent(true);
    setDisableResend(true);
    setResendSecondsLeft(108); // 1:48
  };

  const submitForgotPassword = async (values: FieldValues) => {
    setErrorMessage(undefined);
    requestResetPassword(values.email)
      .then(() => handleSendReset())
      .catch((err) => {
        const message =
          err?.data?.error?.message ?? 'Error while sending request';
        // Set field-level error so the InputField shows red border and inline message
        forgotPassordForm.setError('email', { message });
      });
  };

  const resendLink = () => {
    setErrorMessage(undefined);
    requestResetPassword(userEmail)
      .then(() => {
        setWasResent(true);
        setDisableResend(true);
        setResendSecondsLeft(108);
      })
      .catch((err) => {
        setErrorMessage(
          err?.data?.error?.message ?? 'Error while resending request'
        );
      });
  };

  // Tick countdown each second when disabled
  useEffect(() => {
    if (!disableResend) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    intervalRef.current = window.setInterval(() => {
      setResendSecondsLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setDisableResend(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [disableResend]);

  return {
    forgotPassordForm,
    submitForgotPassword,
    emailSent,
    navigate,
    userEmail,
    resendLink,
    wasResent,
    disableResend,
    resendSecondsLeft,
    errorMessage,
  };
};
